import { useEffect, useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";

/**
 * Full-screen Three.js background layer:
 * A Ferrari (official three.js GLB) that smoothly follows the mouse cursor
 * with a fading shadow trail behind it. Falls back to a procedural low-poly
 * sedan if the GLB fails to load.
 */
export default function CarBackground() {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let width = window.innerWidth;
    let height = window.innerHeight;

    // ---------- Renderer ----------
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    container.appendChild(renderer.domElement);

    // ---------- Scene ----------
    const scene = new THREE.Scene();

    // PMREM generator turns an HDR equirect map into a prefiltered mipmapped
    // radiance environment map that PBR materials can sample for reflections.
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();

    // ---------- Camera (top-down-ish perspective looking at ground plane XZ) ----------
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 2000);
    camera.position.set(0, 120, 90);
    camera.lookAt(0, 0, 0);

    // ---------- Lights ----------
    const ambient = new THREE.AmbientLight(0xffffff, 0.55);
    scene.add(ambient);

    const dirLight = new THREE.DirectionalLight(0xffffff, 1.1);
    dirLight.position.set(60, 120, 40);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.set(1024, 1024);
    dirLight.shadow.camera.near = 1;
    dirLight.shadow.camera.far = 400;
    dirLight.shadow.camera.left = -150;
    dirLight.shadow.camera.right = 150;
    dirLight.shadow.camera.top = 150;
    dirLight.shadow.camera.bottom = -150;
    scene.add(dirLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.35);
    fillLight.position.set(-80, 60, -40);
    scene.add(fillLight);

    // A subtle hemisphere light lifts up the underside so the Ferrari's shaded
    // parts don't look pure black.
    const hemi = new THREE.HemisphereLight(0xffffff, 0x333333, 0.35);
    scene.add(hemi);

    // ---------- Ground plane (invisible, catches shadow) ----------
    const groundGeo = new THREE.PlaneGeometry(4000, 4000);
    const groundMat = new THREE.ShadowMaterial({ opacity: 0.14 });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = 0;
    ground.receiveShadow = true;
    scene.add(ground);

    // ---------- Car container (we swap contents once Ferrari loads) ----------
    // In this container's local space, forward is +X. Any child model that
    // doesn't naturally face +X gets an initial rotation offset applied.
    const car = new THREE.Group();
    scene.add(car);

    // Inner rotator: adds a very slow, constant Y-axis spin ON TOP of the
    // outer `car` group's mouse-follow heading, so the vehicle always feels
    // alive like a showroom turntable, even when the cursor is idle.
    const autoRotator = new THREE.Group();
    car.add(autoRotator);

    // Wheels list — will be repopulated when Ferrari loads.
    let wheels: THREE.Object3D[] = [];
    let wheelRadius = 0.8; // updated after model loads / procedural fallback

    // ---------- Procedural fallback sedan (Kenney Car Kit style) ----------
    // Kept as a fallback if the GLB fails, and shown initially while loading.
    const fallbackCar = new THREE.Group();
    {
      const bodyColor = 0xe4572e;
      const cabinColor = 0xf5f5f5;
      const wheelColor = 0x1a1a1a;
      const glassColor = 0x2b2b2b;
      const lightColor = 0xffe680;

      const bodyMat = new THREE.MeshStandardMaterial({
        color: bodyColor, roughness: 0.55, metalness: 0.15, flatShading: true,
      });
      const cabinMat = new THREE.MeshStandardMaterial({
        color: cabinColor, roughness: 0.5, metalness: 0.1, flatShading: true,
      });
      const wheelMat = new THREE.MeshStandardMaterial({
        color: wheelColor, roughness: 0.9, metalness: 0.05, flatShading: true,
      });
      const glassMat = new THREE.MeshStandardMaterial({
        color: glassColor, roughness: 0.3, metalness: 0.2, flatShading: true,
      });
      const lightMat = new THREE.MeshStandardMaterial({
        color: lightColor, emissive: 0xffcc66, emissiveIntensity: 0.6,
        roughness: 0.4, flatShading: true,
      });

      const chassis = new THREE.Mesh(new THREE.BoxGeometry(6.4, 1.6, 3.2), bodyMat);
      chassis.position.y = 1.4; chassis.castShadow = true; chassis.receiveShadow = true;
      fallbackCar.add(chassis);

      const lowerBody = new THREE.Mesh(new THREE.BoxGeometry(7.2, 1.0, 3.0), bodyMat);
      lowerBody.position.y = 0.9; lowerBody.castShadow = true; lowerBody.receiveShadow = true;
      fallbackCar.add(lowerBody);

      const cabin = new THREE.Mesh(new THREE.BoxGeometry(3.6, 1.5, 2.8), cabinMat);
      cabin.position.set(-0.2, 2.95, 0);
      cabin.castShadow = true; cabin.receiveShadow = true;
      fallbackCar.add(cabin);

      const windshield = new THREE.Mesh(new THREE.BoxGeometry(0.15, 1.35, 2.6), glassMat);
      windshield.position.set(1.7, 2.9, 0);
      windshield.rotation.z = Math.PI * 0.08;
      fallbackCar.add(windshield);

      const rearWindow = new THREE.Mesh(new THREE.BoxGeometry(0.15, 1.35, 2.6), glassMat);
      rearWindow.position.set(-2.05, 2.9, 0);
      rearWindow.rotation.z = -Math.PI * 0.08;
      fallbackCar.add(rearWindow);

      const headlightGeo = new THREE.BoxGeometry(0.3, 0.5, 0.7);
      const hL = new THREE.Mesh(headlightGeo, lightMat);
      hL.position.set(3.25, 1.5, 1.05); fallbackCar.add(hL);
      const hR = new THREE.Mesh(headlightGeo, lightMat);
      hR.position.set(3.25, 1.5, -1.05); fallbackCar.add(hR);

      const tailMat = new THREE.MeshStandardMaterial({
        color: 0xd12b2b, emissive: 0x8a0e0e, emissiveIntensity: 0.5,
        roughness: 0.5, flatShading: true,
      });
      const tL = new THREE.Mesh(headlightGeo, tailMat);
      tL.position.set(-3.65, 1.5, 1.05); fallbackCar.add(tL);
      const tR = new THREE.Mesh(headlightGeo, tailMat);
      tR.position.set(-3.65, 1.5, -1.05); fallbackCar.add(tR);

      const wheelGeo = new THREE.CylinderGeometry(0.8, 0.8, 0.7, 18);
      wheelGeo.rotateX(Math.PI / 2);
      const wheelPositions: [number, number, number][] = [
        [2.1, 0.5, 1.55], [2.1, 0.5, -1.55],
        [-2.1, 0.5, 1.55], [-2.1, 0.5, -1.55],
      ];
      const fallbackWheels: THREE.Mesh[] = [];
      for (const [x, y, z] of wheelPositions) {
        const wheel = new THREE.Mesh(wheelGeo, wheelMat);
        wheel.position.set(x, y, z);
        wheel.castShadow = true;
        wheel.receiveShadow = true;
        fallbackCar.add(wheel);
        fallbackWheels.push(wheel);
      }
      wheels = fallbackWheels;
      wheelRadius = 0.8;
    }
    autoRotator.add(fallbackCar);

    // ---------- Load Ferrari GLB (official three.js example asset) ----------
    // Materials replicate the classic three.js webgl_materials_car demo:
    // clearcoat car paint on body, metallic details on rim/trim, transmissive glass.
    const bodyMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xff2a1f,
      metalness: 1.0,
      roughness: 0.5,
      clearcoat: 1.0,
      clearcoatRoughness: 0.03,
      sheen: 0.5,
    });
    const detailsMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      metalness: 1.0,
      roughness: 0.5,
    });
    const glassMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      metalness: 0.25,
      roughness: 0,
      transmission: 1.0,
      transparent: true,
      opacity: 0.5,
    });

    const FERRARI_URL =
      "https://cdn.jsdelivr.net/gh/mrdoob/three.js@r179/examples/models/gltf/ferrari.glb";
    const DRACO_PATH =
      "https://cdn.jsdelivr.net/gh/mrdoob/three.js@r179/examples/jsm/libs/draco/gltf/";
    const HDR_URL =
      "https://cdn.jsdelivr.net/gh/mrdoob/three.js@r179/examples/textures/equirectangular/venice_sunset_1k.hdr";

    // ---------- Load HDR environment map ----------
    // Uses the same venice_sunset_1k.hdr from the official three.js car demo,
    // so the clearcoat body paint gets real environment reflections.
    let envTexture: THREE.Texture | null = null;
    new RGBELoader().load(
      HDR_URL,
      (hdr) => {
        hdr.mapping = THREE.EquirectangularReflectionMapping;
        const envMap = pmremGenerator.fromEquirectangular(hdr).texture;
        scene.environment = envMap;
        envTexture = envMap;
        hdr.dispose();
      },
      undefined,
      (err) => {
        console.warn("[CarBackground] HDR env map failed to load:", err);
      }
    );

    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath(DRACO_PATH);
    const gltfLoader = new GLTFLoader();
    gltfLoader.setDRACOLoader(dracoLoader);

    let ferrariLoaded = false;
    gltfLoader.load(
      FERRARI_URL,
      (gltf) => {
        const model = gltf.scene.children[0] ?? gltf.scene;

        // Apply demo-style materials by mesh name (body / rim / trim / glass).
        model.traverse((child) => {
          const mesh = child as THREE.Mesh;
          if ((mesh as THREE.Mesh).isMesh) {
            const name = mesh.name.toLowerCase();
            if (name.includes("body")) {
              mesh.material = bodyMaterial;
            } else if (name.includes("rim")) {
              mesh.material = detailsMaterial;
            } else if (name.includes("trim")) {
              mesh.material = detailsMaterial;
            } else if (name.includes("glass")) {
              mesh.material = glassMaterial;
            }
            mesh.castShadow = true;
            mesh.receiveShadow = true;
          }
        });

        // The Ferrari GLB is authored with its front pointing along +Z.
        // Our container's forward convention is +X, so rotate -90° around Y.
        const modelWrapper = new THREE.Group();
        model.rotation.y = -Math.PI / 2;
        modelWrapper.add(model);

        // Normalize model size: bake a scale so length along local +X ≈ 6 units
        // (comparable to the fallback car). Actual on-screen scale is then
        // controlled per-viewport by updateSize().
        const box = new THREE.Box3().setFromObject(modelWrapper);
        const size = new THREE.Vector3();
        box.getSize(size);
        const currentLength = Math.max(size.x, 0.001);
        const normalizeScale = 6 / currentLength;
        modelWrapper.scale.setScalar(normalizeScale);

        // Recompute box after scale and drop model so its wheels touch y=0.
        const box2 = new THREE.Box3().setFromObject(modelWrapper);
        modelWrapper.position.y -= box2.min.y;

        // Collect wheels for spin animation.
        const foundWheels: THREE.Object3D[] = [];
        modelWrapper.traverse((obj) => {
          const n = obj.name.toLowerCase();
          if (n.startsWith("wheel") || n.includes("wheel_")) {
            foundWheels.push(obj);
          }
        });

        // Swap: remove fallback car, add Ferrari, retarget animation refs.
        autoRotator.remove(fallbackCar);
        fallbackCar.traverse((o) => {
          const m = o as THREE.Mesh;
          if (m.isMesh) {
            m.geometry?.dispose?.();
            const mm = m.material;
            if (Array.isArray(mm)) mm.forEach((x) => x.dispose());
            else mm?.dispose?.();
          }
        });
        autoRotator.add(modelWrapper);

        if (foundWheels.length > 0) {
          wheels = foundWheels;
          // Estimate wheel radius from one wheel's bounding box height.
          const wb = new THREE.Box3().setFromObject(foundWheels[0]);
          const wsize = new THREE.Vector3();
          wb.getSize(wsize);
          wheelRadius = Math.max(wsize.y, wsize.z) / 2 || 0.35;
        } else {
          wheels = [];
        }

        ferrariLoaded = true;
        // Re-run sizing so scale reflects the new model's dimensions.
        updateSize();
      },
      undefined,
      (err) => {
        // Network / CORS failure — stick with the procedural fallback.
        console.warn("[CarBackground] Ferrari GLB failed to load:", err);
      }
    );

    // ---------- Shadow trail (fading elongated dark shadows behind the car) ----------
    const TRAIL_COUNT = 32;
    const TRAIL_LIFE_MS = 700;

    const shadowTexture = (() => {
      const size = 128;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d")!;
      const grad = ctx.createRadialGradient(
        size / 2, size / 2, size * 0.05,
        size / 2, size / 2, size / 2
      );
      grad.addColorStop(0, "rgba(0,0,0,0.55)");
      grad.addColorStop(0.5, "rgba(0,0,0,0.25)");
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, size, size);
      const tex = new THREE.CanvasTexture(canvas);
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      return tex;
    })();

    const trailGeo = new THREE.PlaneGeometry(3.0, 1.6);
    trailGeo.rotateX(-Math.PI / 2);

    type TrailPiece = {
      mesh: THREE.Mesh;
      material: THREE.MeshBasicMaterial;
      bornAt: number;
      alive: boolean;
    };
    const trailPieces: TrailPiece[] = [];
    for (let i = 0; i < TRAIL_COUNT; i++) {
      const mat = new THREE.MeshBasicMaterial({
        map: shadowTexture,
        color: 0x000000,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      });
      const m = new THREE.Mesh(trailGeo, mat);
      m.position.set(0, 0.02, 0);
      m.visible = false;
      scene.add(m);
      trailPieces.push({ mesh: m, material: mat, bornAt: 0, alive: false });
    }
    let trailCursor = 0;

    // ---------- Mouse → ground-plane mapping ----------
    const target = new THREE.Vector3(0, 0, 0);
    const carPos = new THREE.Vector3(0, 0, 0);
    let prevX = 0;
    let prevZ = 0;
    let currentHeading = 0;
    let targetHeading = 0;
    let lastHeading = 0;

    let worldHalfW = 100;
    let worldHalfH = 60;

    // Scroll-driven scale multiplier: 1.0 at top → up to ~2.0 when scrolled
    // one full viewport down. Smoothly interpolated toward the target so it
    // feels physical, not snappy.
    let baseScale = 1;              // scale computed from viewport size
    let scrollScaleTarget = 1;      // multiplier we want based on scrollY
    let scrollScaleCurrent = 1;     // interpolated current multiplier
    const MAX_SCROLL_ZOOM = 2.0;    // car grows up to 2x on full scroll
    const SCROLL_RANGE_PX = 800;    // how many px of scroll to reach max zoom

    function recomputeWorldExtents() {
      const distance = camera.position.length();
      const vFov = (camera.fov * Math.PI) / 180;
      const visibleHeight = 2 * Math.tan(vFov / 2) * distance;
      const visibleWidth = visibleHeight * camera.aspect;
      worldHalfW = visibleWidth * 0.55;
      worldHalfH = visibleHeight * 0.4;
    }

    function updateSize() {
      width = window.innerWidth;
      height = window.innerHeight;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      recomputeWorldExtents();

      const minDim = Math.min(width, height);
      const targetScreenSize = minDim * 0.42;
      const worldPerPx = (worldHalfH * 2) / height;
      const desiredWorldLength = targetScreenSize * worldPerPx * 2.2;
      // Both fallback and normalized Ferrari have local length ≈ 6-7 units.
      const baseLength = ferrariLoaded ? 6 : 7.2;
      const scale = Math.max(4.0, Math.min(18.0, desiredWorldLength / baseLength));
      baseScale = scale;
      car.scale.setScalar(baseScale * scrollScaleCurrent);
    }

    function onScroll() {
      const y = Math.max(0, window.scrollY || window.pageYOffset || 0);
      const t = Math.min(1, y / SCROLL_RANGE_PX);
      // Ease-out curve so growth feels smooth near the top and settles at max.
      const eased = 1 - Math.pow(1 - t, 2);
      scrollScaleTarget = 1 + (MAX_SCROLL_ZOOM - 1) * eased;
    }

    updateSize();

    carPos.set(0, 0, 0);
    car.position.copy(carPos);

    function onMouseMove(e: MouseEvent) {
      const nx = (e.clientX / width) * 2 - 1;
      const ny = (e.clientY / height) * 2 - 1;
      target.x = nx * worldHalfW;
      target.z = ny * worldHalfH;
    }

    function onResize() {
      updateSize();
    }

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    // ---------- Animation ----------
    const LERP = 0.08;
    const HEADING_LERP = 0.15;
    const SPEED_SQ_THRESHOLD = 0.5;
    const TRAIL_EVERY_FRAMES = 4;
    let frameCounter = 0;
    let rafId = 0;
    let wheelSpin = 0;

    const qCurrent = new THREE.Quaternion();
    const qTarget = new THREE.Quaternion();
    const yAxis = new THREE.Vector3(0, 1, 0);

    function tick() {
      rafId = requestAnimationFrame(tick);
      frameCounter++;

      carPos.x += (target.x - carPos.x) * LERP;
      carPos.z += (target.z - carPos.z) * LERP;

      const dx = carPos.x - prevX;
      const dz = carPos.z - prevZ;
      const speedSq = dx * dx + dz * dz;

      if (speedSq >= SPEED_SQ_THRESHOLD) {
        targetHeading = -Math.atan2(dz, dx);
        lastHeading = targetHeading;
      } else {
        targetHeading = lastHeading;
      }

      qCurrent.setFromAxisAngle(yAxis, currentHeading);
      qTarget.setFromAxisAngle(yAxis, targetHeading);
      qCurrent.slerp(qTarget, HEADING_LERP);
      currentHeading = 2 * Math.atan2(qCurrent.y, qCurrent.w);

      car.position.copy(carPos);
      car.quaternion.copy(qCurrent);

      // Smoothly ease the current scroll-driven scale toward the target so
      // rapid wheel/trackpad flicks translate into a gentle zoom.
      scrollScaleCurrent += (scrollScaleTarget - scrollScaleCurrent) * 0.12;
      car.scale.setScalar(baseScale * scrollScaleCurrent);

      // Wheel spin based on actual speed (arc length = radius * angle).
      const speed = Math.sqrt(speedSq);
      const scaleFactor = car.scale.x || 1;
      const spinDelta = (speed / (wheelRadius * scaleFactor)) * 0.9;
      wheelSpin += spinDelta;
      for (const w of wheels) {
        // Ferrari wheels spin around their local X axis; fallback wheels use Z.
        // We set both — the unused axis has no visible effect on the other model.
        if (ferrariLoaded) {
          w.rotation.x = wheelSpin;
        } else {
          w.rotation.z = wheelSpin;
        }
      }

      prevX = carPos.x;
      prevZ = carPos.z;

      if (
        frameCounter % TRAIL_EVERY_FRAMES === 0 &&
        speedSq >= SPEED_SQ_THRESHOLD * 0.3
      ) {
        const piece = trailPieces[trailCursor];
        trailCursor = (trailCursor + 1) % TRAIL_COUNT;
        const backOffset = 2.2 * car.scale.x;
        piece.mesh.position.set(
          carPos.x - Math.cos(currentHeading) * backOffset,
          0.02,
          carPos.z + Math.sin(currentHeading) * backOffset
        );
        piece.mesh.rotation.y = currentHeading;
        piece.mesh.scale.setScalar(car.scale.x * 1.05);
        piece.mesh.visible = true;
        piece.bornAt = performance.now();
        piece.alive = true;
        piece.material.opacity = 0.45;
      }

      const now = performance.now();
      for (const p of trailPieces) {
        if (!p.alive) continue;
        const age = now - p.bornAt;
        const t = age / TRAIL_LIFE_MS;
        if (t >= 1) {
          p.material.opacity = 0;
          p.mesh.visible = false;
          p.alive = false;
        } else {
          p.material.opacity = 0.45 * (1 - t);
        }
      }

      renderer.render(scene, camera);
    }
    tick();

    // ---------- Cleanup ----------
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);

      scene.traverse((obj) => {
        const anyObj = obj as THREE.Mesh;
        if ((anyObj as THREE.Mesh).isMesh) {
          const geom = (anyObj as THREE.Mesh).geometry;
          if (geom) geom.dispose();
          const mat = (anyObj as THREE.Mesh).material;
          if (Array.isArray(mat)) {
            mat.forEach((m) => m.dispose());
          } else if (mat) {
            (mat as THREE.Material).dispose();
          }
        }
      });
      shadowTexture.dispose();
      bodyMaterial.dispose();
      detailsMaterial.dispose();
      glassMaterial.dispose();
      dracoLoader.dispose();
      if (envTexture) envTexture.dispose();
      pmremGenerator.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    />
  );
}